import { decode } from 'he';
import { useRef, useState } from 'react';
import Truncate from 'react-truncate';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { ClampedLinkWorkaround } from '@hivebrite/design-system/Dotdotdot';
import { linkControlledHoverEffect } from '@hivebrite/design-system/Theme';
import { onEnterOrSpacePress } from '@hivebrite/utils';

import { htmlToString } from 'hb-react/frontoffice/utils/globalUtils';

import { type NewsCardProps } from './types';

export const NewsCard = ({
  coverPicture,
  description,
  fallbackCoverPicture,
  onClick,
  publishedAt,
  subject,
}: NewsCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const descRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const cleanDesc = decode(htmlToString(description));

  // Shorten the description to reduce the calculations of react-truncate
  // SHORTENED_DESCRIPTION_LENGTH is arbitrary, it was measured to be a bit above
  // the max number of displayed characters on mobile & desktop
  const SHORTENED_DESCRIPTION_LENGTH = 400;
  const shortenedDescription = cleanDesc?.slice(
    0,
    SHORTENED_DESCRIPTION_LENGTH,
  );

  const handleFocus = (value: boolean) => setIsFocused(value);
  const handleHover = (value: boolean) => setIsHovered(value);

  const isExpanded = isHovered || isFocused;

  return (
    <CardStyled
      onClick={onClick}
      onKeyPress={onEnterOrSpacePress(onClick)}
      onMouseEnter={() => handleHover(true)}
      onMouseLeave={() => handleHover(false)}
      onFocus={() => handleFocus(true)}
      onBlur={() => handleFocus(false)}
      ref={cardRef}
      role="link"
      tabIndex={0}
    >
      <ImgWrapper dataTestId="news-cover-wrapper">
        <ImgStyled
          alt={subject}
          as="img"
          onError={(e) => {
            if (fallbackCoverPicture) {
              (e.target as HTMLImageElement).src = fallbackCoverPicture;
            }
          }}
          src={coverPicture}
        />
      </ImgWrapper>

      <ContentWrapper>
        <AnimatedContentBox>
          <Box data-testid="link-to-news">
            <LinkStyled>
              <ClampedLinkWorkaround
                clamp={2}
                lineHeight="2rem"
                margin={0}
                padding={0}
              >
                {subject}
              </ClampedLinkWorkaround>
            </LinkStyled>
          </Box>

          {publishedAt && (
            <DateStyled $description={cleanDesc}>{publishedAt}</DateStyled>
          )}

          {shortenedDescription && (
            <Description font="body-regular-asphalt" ref={descRef}>
              <Truncate lines={isExpanded ? 4 : 2}>
                {shortenedDescription}
              </Truncate>
            </Description>
          )}
        </AnimatedContentBox>
      </ContentWrapper>
    </CardStyled>
  );
};

const commonContentStyles = {
  pb: { _: 4, xl: 6 },
  pt: 3,
};

const ContentWrapper = styled(Box).attrs({
  ...commonContentStyles,
  height: { _: '116px', xl: '118px' },
})`
  // Inherit Card component's background
  background: inherit;
  box-sizing: content-box;
  border-radius: ${({ theme }) => {
    const br = theme.themeVariables.globalBorderRadius;

    return `0 0 ${br}px ${br}px`;
  }};
`;

const AnimatedContentBox = styled(Box).attrs(() => ({
  ...commonContentStyles,
  height: { _: '116px', xl: '118px' },
  flex: 1,
  flexDirection: 'column',
  px: { _: 3, l: 4 },
  zIndex: 0,
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  overflow: 'hidden',
}))`
  // Inherit Card component's background
  background: inherit;
  transition: height 0.3s;
  box-sizing: content-box;
  border-radius: ${({ theme }) => {
    const br = theme.themeVariables.globalBorderRadius;

    return `0 0 ${br}px ${br}px`;
  }};
`;

const ImgStyled = styled(Box).attrs({
  width: '100%',
})`
  // Inherit Card component's background
  background: inherit;
  cursor: pointer;
  object-fit: cover;
  transition-duration: 0.3s;
  transition-property: filter, height, transform;
  aspect-ratio: 1600 / 600;
`;

const ImgWrapper = styled(Box).attrs({
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
})`
  // Inherit Card component's background
  background: inherit;
  background-image: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.6));
  border-radius: ${({ theme }) => {
    const br = theme.themeVariables.globalBorderRadius;

    return `${br}px ${br}px 0 0`;
  }};
`;

const LinkStyled = styled(Box).attrs({
  as: 'h4',
  font: 'h4-semibold',
  marginTop: '0 !important', // Overwrite PO h3 style that breaks the card layout
  marginBottom: '0 !important', // Overwrite PO h3 style that breaks the card layout
})`
  text-decoration: none;
`;

const DateStyled = styled(Box).attrs<{ $description?: string }>(
  ({ $description }) => ({
    font: 'metadata-semibold-asphalt',
    mt: 1,
    pb: $description && '6px',
  }),
)<{ $description?: string }>``;

const Description = styled(Box)`
  line-height: 1.5rem;
`;

const CardStyled = styled(Card)`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  padding: 0;
  position: relative;

  @media (hover: hover) {
    & ${ImgStyled} {
      transform: scale(1);
    }

    &:hover ${ImgStyled}, &:focus ${ImgStyled} {
      transform: scale(1.05);
    }

    &:hover ${LinkStyled}, &:focus ${LinkStyled} {
      ${linkControlledHoverEffect()}
    }

    &:hover ${AnimatedContentBox}, &:focus ${AnimatedContentBox} {
      height: 157px;
    }
  }
`;
